import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db as firestore } from '../../config/firebase';
import { collection, getDocs, query, orderBy, doc, getDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';
import { getAssignedStores } from '../../utils/storeAssignment';
import type { Store as StoreType } from '../../@types/store';
import { Star, Globe } from 'lucide-react';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${ThemeColors.background.default};
`;

const MainContent = styled.main`
  padding: ${Spacing.md} ${Spacing.md} ${Spacing.xxl};
  padding-top: calc(${Spacing.lg} + 64px); /* Account for fixed header */
`;

const PageTitle = styled.h1`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.md} 0;
  padding-bottom: ${Spacing.sm};
  border-bottom: 2px solid ${ThemeColors.primary.light};
`;

const TabContainer = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.lg};
  border-bottom: 2px solid ${ThemeColors.border.light};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Tab = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background-color: ${props => props.active ? ThemeColors.background.paper : 'transparent'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? ThemeColors.primary.main : 'transparent'};
  cursor: pointer;
  font-size: ${Typography.fontSize.md};
  font-weight: ${props => props.active ? Typography.fontWeight.bold : Typography.fontWeight.medium};
  color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.text.secondary};
  transition: all ${Transitions.duration.short} ease;
  position: relative;
  margin-bottom: -2px;
  white-space: nowrap;

  &:active {
    background-color: ${ThemeColors.background.default};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TabBadge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 ${Spacing.xs};
  background-color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.gray[300]};
  color: ${props => props.active ? '#fff' : ThemeColors.text.secondary};
  border-radius: 11px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  margin-left: ${Spacing.xs};
`;

type TabType = 'assigned' | 'all';

const SearchSection = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const SearchContainer = styled.div`
  position: relative;
  margin-top: ${Spacing.md};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.md} ${Spacing.lg};
  padding-left: 48px;
  border-radius: ${BorderRadius.lg};
  border: 1px solid ${ThemeColors.border.light};
  font-size: ${Typography.fontSize.md};
  background-color: ${ThemeColors.background.paper};
  box-shadow: ${Shadows.sm};
  transition: all ${Transitions.duration.short} ease;
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: ${Shadows.md};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${ThemeColors.text.secondary};
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const StoreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg};
`;

const StoreCard = styled.div`
  background-color: ${ThemeColors.background.paper};
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  box-shadow: ${Shadows.sm};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ease;
  border-left: 4px solid ${ThemeColors.primary.main};
  
  &:active {
    transform: translateY(2px);
    box-shadow: ${Shadows.xs};
  }
`;

const StoreName = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.xs} 0;
  line-height: 1.3;
`;

const CustomerNumberBadge = styled.div`
  display: inline-block;
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.primary.main};
  font-weight: ${Typography.fontWeight.semiBold};
  margin-bottom: ${Spacing.md};
  background-color: ${ThemeColors.primary.light}20;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.md};
`;

const InfoSection = styled.div`
  margin: ${Spacing.md} 0;
`;

const StoreAddress = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin: 0 0 ${Spacing.sm} 0;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  
  svg {
    min-width: 20px;
    margin-right: ${Spacing.sm};
    margin-top: 3px;
    color: ${ThemeColors.text.hint};
  }
`;

const StorePhone = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin: 0;
  font-weight: ${Typography.fontWeight.medium};
  display: flex;
  align-items: center;
  
  svg {
    min-width: 20px;
    margin-right: ${Spacing.sm};
    color: ${ThemeColors.text.hint};
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${ThemeColors.border.light};
  margin: ${Spacing.md} 0;
`;

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${Spacing.md};
  margin-top: ${Spacing.md};
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' | 'tertiary' }>`
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.medium};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ease;
  
  background-color: ${props => 
    props.variant === 'primary' ? ThemeColors.primary.main :
    props.variant === 'secondary' ? ThemeColors.secondary.main : 
    ThemeColors.background.paper
  };
  
  color: ${props => 
    props.variant === 'primary' ? ThemeColors.primary.contrastText :
    props.variant === 'secondary' ? ThemeColors.secondary.contrastText : 
    ThemeColors.primary.main
  };
  
  border: ${props => 
    props.variant === 'tertiary' ? `1px solid ${ThemeColors.primary.main}` : 'none'
  };
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const ButtonText = styled.span`
  margin-top: ${Spacing.xs};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  gap: ${Spacing.md};
`;

const LoadingSpinner = styled.div`
  border: 3px solid ${ThemeColors.gray[200]};
  border-top: 3px solid ${ThemeColors.primary.main};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xl} 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.md};
`;

const EmptyStateIcon = styled.div`
  color: ${ThemeColors.gray[300]};
  margin-bottom: ${Spacing.md};
  
  svg {
    width: 64px;
    height: 64px;
  }
`;

const EmptyStateText = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

interface Store {
  id: string;
  CustomerNo: string;
  StoreName: string;
  Address: string;
  City: string;
  Province: string;
  PostalCode: string;
  Phone: string;
  Email: string;
  StoreType: string;
}

export const StoreVisitScreenMobile = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [assignedStores, setAssignedStores] = useState<Store[]>([]);
  const [filteredStores, setFilteredStores] = useState<Store[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('assigned');
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isHandLead = userRole === 'hand-lead';

  useEffect(() => {
    const fetchStores = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const storesRef = collection(firestore, 'Stores');
        const q = query(storesRef, orderBy('StoreName'));
        const querySnapshot = await getDocs(q);
        
        const storesData: Store[] = [];
        const storeTypesData: StoreType[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Skip archived (soft-deleted) stores so they don't appear in Store Visit
          if ((data.status || data.Status) === 'archived') return;
          storesData.push({
            id: doc.id,
            ...data as Omit<Store, 'id'>
          });
          
          // Build StoreType array for hand-lead users
          if (isHandLead) {
            storeTypesData.push({
              id: doc.id,
              CustomerNo: data.CustomerNo || '',
              StoreName: data.StoreName || 'Unknown Store',
              Address: data.Address || '',
              City: data.City || '',
              Prov: data.Prov || '',
              PostalCode: data.PostalCode || '',
              Telephone: data.Telephone || '',
              EmailAdddress: data.EmailAdddress || '',
              SalespersonNo: data.SalespersonNo || '',
              salesGroup: data.SalesGroup || '',
              assignedToUserIds: data.assignedToUserIds || [],
              status: 'active',
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            });
          }
        });
        
        setStores(storesData);
        
        // For hand-lead users, also load assigned stores
        if (isHandLead && user.id) {
          const userDoc = await getDoc(doc(firestore, 'users', user.id));
          const userData = userDoc.data();

          const assigned = await getAssignedStores(
            user.id, 
            storeTypesData, 
            userData?.salesGroup,
            userData?.assignedStoreIds
          );
          
          const assignedData: Store[] = assigned.map(s => ({
            id: s.id,
            CustomerNo: s.CustomerNo || '',
            StoreName: s.StoreName || 'Unknown Store',
            Address: s.Address || '',
            City: s.City || '',
            Province: s.Prov || '',
            PostalCode: s.PostalCode || '',
            Phone: s.Telephone || '',
            Email: s.EmailAdddress || '',
            StoreType: s.salesGroup || ''
          }));

          console.log(`Assigned stores for hand-lead user ${user.id}:`, assignedData.length);
          setAssignedStores(assignedData);
          setFilteredStores(activeTab === 'assigned' ? assignedData : storesData);
        } else {
          setFilteredStores(storesData);
        }
      } catch (error) {
        console.error('Error fetching stores:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStores();
  }, [user, isHandLead, activeTab]);

  useEffect(() => {
    // Determine which store list to filter based on active tab for hand-lead
    const sourceStores = (isHandLead && activeTab === 'assigned') ? assignedStores : stores;
    
    if (searchQuery.trim() === '') {
      setFilteredStores(sourceStores);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = sourceStores.filter(store => 
        (store.StoreName || '').toLowerCase().includes(query) ||
        (store.CustomerNo || '').toLowerCase().includes(query) ||
        (store.City || '').toLowerCase().includes(query) ||
        (store.Address || '').toLowerCase().includes(query)
      );
      setFilteredStores(filtered);
    }
  }, [searchQuery, stores, assignedStores, activeTab, isHandLead]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchQuery(''); // Clear search when switching tabs
  };

  const handleStoreVisit = (storeId: string) => {
    navigate(`/manager/store-details/${storeId}?tab=store-visit`);
  };

  const handleCall = (phone: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.location.href = `tel:${phone}`;
  };

  const handleFind = (address: string, city: string, province: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Filter out any undefined values and join with commas
    const locationParts = [address, city, province].filter(part => part && part !== 'undefined');
    const query = encodeURIComponent(locationParts.join(', '));
    window.open(`https://maps.google.com/?q=${query}`, '_blank');
  };

  return (
    <PageWrapper>
      <MainContent>
        <PageTitle>Stores</PageTitle>

        {/* Tabs for hand-lead users - show assigned vs all stores */}
        {isHandLead && (
          <TabContainer>
            <Tab 
              active={activeTab === 'assigned'} 
              onClick={() => handleTabChange('assigned')}
            >
              <Star size={18} />
              {t('storeVisit.myStores', 'My Stores')}
              <TabBadge active={activeTab === 'assigned'}>
                {assignedStores.length}
              </TabBadge>
            </Tab>
            <Tab 
              active={activeTab === 'all'} 
              onClick={() => handleTabChange('all')}
            >
              <Globe size={18} />
              {t('storeVisit.allStores', 'All Stores')}
              <TabBadge active={activeTab === 'all'}>
                {stores.length}
              </TabBadge>
            </Tab>
          </TabContainer>
        )}
        
        <SearchSection>
          <SearchContainer>
            <SearchIcon>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </SearchIcon>
            <SearchInput 
              placeholder="Search by customer#, store name, city, or address..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchContainer>
        </SearchSection>
        
        {isLoading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Loading stores...</LoadingText>
          </LoadingContainer>
        ) : filteredStores.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </EmptyStateIcon>
            <EmptyStateText>No stores found matching your search.</EmptyStateText>
          </EmptyState>
        ) : (
          <StoreList>
            {filteredStores.map(store => (
              <StoreCard key={store.id} onClick={() => handleStoreVisit(store.id)}>
                <StoreName>{store.StoreName}</StoreName>
                <CustomerNumberBadge>#{store.CustomerNo}</CustomerNumberBadge>
                
                <InfoSection>
                  <StoreAddress>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    {[store.Address, store.City, store.Province, store.PostalCode].filter(part => part && part !== 'undefined').join(', ')}
                  </StoreAddress>
                  
                  {store.Phone && (
                    <StorePhone>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                      {store.Phone}
                    </StorePhone>
                  )}
                </InfoSection>
                
                <Divider />
                
                <ActionButtons>
                  <ActionButton 
                    variant="tertiary"
                    onClick={(e) => handleCall(store.Phone, e)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    <ButtonText>Call</ButtonText>
                  </ActionButton>
                  
                  <ActionButton 
                    variant="secondary"
                    onClick={(e) => handleFind(store.Address, store.City, store.Province, e)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <ButtonText>Find</ButtonText>
                  </ActionButton>
                  
                  <ActionButton 
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStoreVisit(store.id);
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                      <polyline points="10 17 15 12 10 7"></polyline>
                      <line x1="15" y1="12" x2="3" y2="12"></line>
                    </svg>
                    <ButtonText>Visit</ButtonText>
                  </ActionButton>
                </ActionButtons>
              </StoreCard>
            ))}
          </StoreList>
        )}
      </MainContent>
    </PageWrapper>
  );
};